import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  type DocumentData,
  type FirestoreError,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "../lib/firebase";

/**
 * Firestore koleksiyon dinleyicisi.
 *
 * İKİ ÇAĞRI BİÇİMİ DE DESTEKLENİR (modüller arası uyum için):
 *   1) useCollection<T>("orders", [where("erpStatus","==",s), orderBy("createdAt","desc")])
 *   2) useCollection<T>(() => query(collection(db,"orders"), ...), [deps])
 *
 * Biçim 2 gereklidir çünkü sorgu bazen HİÇ kurulmamalıdır (yetkisiz rol, eksik
 * parametre). `null` dönen fabrika = dinleyici açılmaz. Eski panelde yetkisiz
 * kullanıcı sorguyu yine de tetikliyor, sonra login'e atılıyordu (G-13).
 *
 * Dönen nesnede hem `docs` hem `data` vardır; ikisi aynı diziyi gösterir.
 */
export interface CollectionState<T> {
  /** Doküman listesi (her elemanda `id` alanı vardır). */
  docs: Array<T & { id: string }>;
  /** `docs` ile aynı dizi — diğer modüllerin kullandığı ad. */
  data: Array<T & { id: string }>;
  loading: boolean;
  /** Türkçe, kullanıcıya doğrudan gösterilebilir hata. Yoksa null. */
  error: string | null;
  empty: boolean;
}

/** Firestore hata kodlarının Türkçe karşılığı (kullanıcıya gösterilir). */
export function firestoreErrorMessage(e: FirestoreError | { code?: string } | unknown): string {
  const code =
    typeof e === "object" && e !== null && typeof (e as { code?: unknown }).code === "string"
      ? (e as { code: string }).code
      : "unknown";
  switch (code) {
    case "permission-denied":
      return "Bu kaydı görme yetkiniz yok.";
    case "unauthenticated":
      return "Oturumunuz sona ermiş. Lütfen tekrar giriş yapın.";
    case "failed-precondition":
      // Genellikle eksik bileşik indeks. Geliştiriciye konsolda tam metin kalır.
      return "Bu sorgu için gerekli veritabanı indeksi eksik. Yöneticinize bildirin.";
    case "unavailable":
      return "Sunucuya ulaşılamıyor. İnternet bağlantınızı kontrol edin.";
    case "resource-exhausted":
      return "Sistem şu anda yoğun. Lütfen birkaç saniye sonra tekrar deneyin.";
    case "deadline-exceeded":
      return "İstek zaman aşımına uğradı. Lütfen tekrar deneyin.";
    case "not-found":
      return "Kayıt bulunamadı.";
    default:
      return `Veri okunamadı (${code}).`;
  }
}

/** Diğer modüllerin beklediği kısa ad. */
export const errorMessage = firestoreErrorMessage;

/**
 * Kısıt dizisinden kararlı bir anahtar üretir.
 * Kısıt nesneleri her render'da yeniden yaratıldığı için referansla
 * karşılaştırılamaz; anahtar değişmedikçe dinleyici yeniden kurulmaz.
 */
function constraintKey(constraints: readonly QueryConstraint[]): string {
  try {
    return JSON.stringify(
      constraints.map((c) => {
        const anyC = c as unknown as Record<string, unknown>;
        // Alan adları SDK sürümüne göre değişebilir; hepsi denenir.
        const parts = Object.keys(anyC)
          .filter((k) => typeof anyC[k] !== "function")
          .map((k) => `${k}:${safeValue(anyC[k])}`);
        return `${c.type}|${parts.join(",")}`;
      }),
    );
  } catch {
    // Serileştirilemeyen kısıt: en azından tip dizisi anahtar olsun.
    return constraints.map((c) => c.type).join("|");
  }
}

function safeValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return "[obj]";
    }
  }
  return String(v);
}

type Builder = () => Query<DocumentData> | null;

export function useCollection<T = DocumentData>(
  source: string | Builder,
  arg2?: readonly QueryConstraint[] | readonly unknown[],
): CollectionState<T> {
  const [docs, setDocs] = useState<Array<T & { id: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isPath = typeof source === "string";
  const constraints = (isPath ? (arg2 as readonly QueryConstraint[] | undefined) ?? [] : []) as
    readonly QueryConstraint[];

  // Fabrika biçiminde bağımlılıklar çağıranın verdiği diziden gelir;
  // yol biçiminde yol + kısıt anahtarından türetilir.
  const deps: readonly unknown[] = isPath
    ? [source, constraintKey(constraints)]
    : ((arg2 as readonly unknown[] | undefined) ?? []);

  // Fabrika/kısıtlar her render'da yeniden üretilir; effect'in bunlara
  // bağlanmaması için ref'te tutulur.
  const sourceRef = useRef<string | Builder>(source);
  sourceRef.current = source;
  const constraintsRef = useRef<readonly QueryConstraint[]>(constraints);
  constraintsRef.current = constraints;

  useEffect(() => {
    const s = sourceRef.current;
    const q: Query<DocumentData> | null =
      typeof s === "string"
        ? query(collection(db, s), ...constraintsRef.current)
        : s();

    if (!q) {
      setDocs([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const unsub = onSnapshot(
      q,
      (snap) => {
        setDocs(
          snap.docs.map((d: QueryDocumentSnapshot<DocumentData>) => ({
            ...(d.data() as T),
            id: d.id,
          })),
        );
        setLoading(false);
      },
      (e: FirestoreError) => {
        // Sessiz başarısızlık yok: hata ekrana çıkar, konsola tam hâliyle düşer.
        console.error("Firestore sorgu hatası", e);
        setError(firestoreErrorMessage(e));
        setDocs([]);
        setLoading(false);
      },
    );

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return useMemo(
    () => ({
      docs,
      data: docs,
      loading,
      error,
      empty: !loading && !error && docs.length === 0,
    }),
    [docs, loading, error],
  );
}
